import { useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../utils/cn'
import { FormField } from './FormField'
import { CONTROL_BASE, controlTone, describedBy } from './fieldStyles'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: ReactNode
  id?: string
  hint?: ReactNode
  error?: ReactNode
  /** Material Symbols glyph name rendered inside the field, on the left. */
  leadingIcon?: string
  containerClassName?: string
}

export function Input({
  label,
  id,
  hint,
  error,
  leadingIcon,
  required,
  className,
  containerClassName,
  ...rest
}: InputProps) {
  const autoId = useId()
  const fieldId = id ?? autoId

  return (
    <FormField
      id={fieldId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      <div className="relative">
        {leadingIcon && (
          <span className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[20px] text-outline">
            {leadingIcon}
          </span>
        )}
        <input
          {...rest}
          id={fieldId}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(fieldId, hint, error)}
          className={cn(CONTROL_BASE, controlTone(Boolean(error)), leadingIcon && 'pl-12', className)}
        />
      </div>
    </FormField>
  )
}
